/**
 * @module NotificationPreferencesForm
 * @package @geenius/notifications-solidjs-css
 * @description Renders the shared preference controls for the SolidJS vanilla
 * CSS variant.
 */

import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  getNotificationChannelLabel,
  getNotificationTypeLabel,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { For } from 'solid-js'
import { cx } from '../lib/cx'
import { createNotificationCopy } from '../lib/notificationCopy'

/**
 * Async or sync handler used by the preferences form.
 */
export type NotificationPreferencesFormUpdate = (
  channels?: Record<NotificationChannel, boolean>,
  types?: Record<NotificationType, boolean>,
  quietHours?: { start: string; end: string },
) => void | Promise<void>

/**
 * Props for the SolidJS CSS preferences form.
 */
export interface NotificationPreferencesFormProps {
  preferences: NotificationPreferences
  onUpdate: NotificationPreferencesFormUpdate
  class?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']

/**
 * Renders the notification preferences form.
 *
 * @param props Preference data and update handler.
 * @returns The preference form markup.
 */
export function NotificationPreferencesForm(
  props: NotificationPreferencesFormProps,
): JSX.Element {
  const { t } = createNotificationCopy()
  const quietHours = () => props.preferences.quietHours ?? { start: '22:00', end: '07:00' }

  return (
    <form
      class={cx('notifications__preferences', props.class)}
      aria-label={t('notifications.preferences.formAria')}
    >
      <div class={cx('notifications__preferences-section')}>
        <h3 class={cx('notifications__preferences-title')}>
          {t('notifications.preferences.sections.channels')}
        </h3>
        <div class={cx('notifications__preferences-list')}>
          <For each={CHANNELS}>
            {(channel) => {
              const channelLabel = getNotificationChannelLabel(channel, t)

              return (
                <div class={cx('notifications__preferences-row')}>
                <span class={cx('notifications__preferences-label')}>
                    {channelLabel}
                </span>
                <button
                  type="button"
                  class={cx(
                    'notifications__preferences-toggle',
                    props.preferences.channels[channel]
                      ? 'notifications__preferences-toggle--on'
                      : 'notifications__preferences-toggle--off',
                  )}
                  aria-pressed={props.preferences.channels[channel]}
                  aria-label={t('notifications.preferences.toggleChannelAria', {
                    channel: channelLabel,
                  })}
                  onClick={() =>
                    void props.onUpdate({
                      ...props.preferences.channels,
                      [channel]: !props.preferences.channels[channel],
                    })
                  }
                >
                  <span class={cx('notifications__preferences-toggle-knob')} />
                </button>
                </div>
              )
            }}
          </For>
        </div>
      </div>
      <div class={cx('notifications__preferences-section')}>
        <h3 class={cx('notifications__preferences-title')}>
          {t('notifications.preferences.sections.types')}
        </h3>
        <div class={cx('notifications__preferences-list')}>
          <For each={NOTIFICATION_TYPES}>
            {(type) => {
              const typeLabel = getNotificationTypeLabel(type, t)

              return (
                <div class={cx('notifications__preferences-row')}>
                <span class={cx('notifications__preferences-label')}>
                    {TYPE_CONFIG[type].icon} {typeLabel}
                </span>
                <button
                  type="button"
                  class={cx(
                    'notifications__preferences-toggle',
                    props.preferences.types[type]
                      ? 'notifications__preferences-toggle--on'
                      : 'notifications__preferences-toggle--off',
                  )}
                  aria-pressed={props.preferences.types[type]}
                  aria-label={t('notifications.preferences.toggleTypeAria', {
                    type: typeLabel,
                  })}
                  onClick={() =>
                    void props.onUpdate(undefined, {
                      ...props.preferences.types,
                      [type]: !props.preferences.types[type],
                    })
                  }
                >
                  <span class={cx('notifications__preferences-toggle-knob')} />
                </button>
                </div>
              )
            }}
          </For>
        </div>
      </div>
      <div class={cx('notifications__preferences-section')}>
        <h3 class={cx('notifications__preferences-title')}>
          {t('notifications.preferences.sections.quietHours')}
        </h3>
        <div class={cx('notifications__preferences-quiet')}>
          <label
            class={cx('notifications__preferences-field')}
            for="notifications-css-solid-quiet-start"
          >
            {t('notifications.preferences.fields.start')}
            <input
              id="notifications-css-solid-quiet-start"
              class={cx('notifications__preferences-time-input')}
              type="time"
              value={quietHours().start}
              onInput={(event) =>
                void props.onUpdate(undefined, undefined, {
                  start: event.currentTarget.value,
                  end: quietHours().end,
                })
              }
            />
          </label>
          <label
            class={cx('notifications__preferences-field')}
            for="notifications-css-solid-quiet-end"
          >
            {t('notifications.preferences.fields.end')}
            <input
              id="notifications-css-solid-quiet-end"
              class={cx('notifications__preferences-time-input')}
              type="time"
              value={quietHours().end}
              onInput={(event) =>
                void props.onUpdate(undefined, undefined, {
                  start: quietHours().start,
                  end: event.currentTarget.value,
                })
              }
            />
          </label>
        </div>
      </div>
    </form>
  )
}
